import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaBuilding, FaUsers, FaAward, FaHandshake } from 'react-icons/fa';

const AboutPreview: React.FC<{ language: 'ar' | 'en' }> = ({ language }) => {
  const features = [
    {
      icon: FaBuilding,
      title: language === 'ar' ? 'خبرة 11+ سنة' : '11+ Years Experience',
      description: language === 'ar' ? 'خبرة عريقة في مجال البناء والتشييد' : 'Extensive experience in construction and contracting'
    },
    {
      icon: FaUsers,
      title: language === 'ar' ? 'فريق محترف' : 'Professional Team',
      description: language === 'ar' ? 'مهندسون وفنيون ذوو كفاءة عالية' : 'Highly qualified engineers and technicians'
    },
    {
      icon: FaAward,
      title: language === 'ar' ? 'جودة عالية' : 'High Quality',
      description: language === 'ar' ? 'نلتزم بأعلى معايير الجودة والسلامة' : 'We adhere to the highest standards of quality and safety'
    },
    {
      icon: FaHandshake,
      title: language === 'ar' ? 'شراكة دائمة' : 'Long-term Partnership',
      description: language === 'ar' ? 'نبني علاقات طويلة المدى مع عملائنا' : 'We build long-term relationships with our clients'
    }
  ];

  return (
    <section id="about-preview" className="section-padding bg-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="mb-6">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4"
              >
                {language === 'ar' ? 'من نحن' : 'About Us'}
              </motion.span>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
              >
                {language === 'ar' ? 'نبني المستقبل' : 'Building the Future'}
                <span className="text-gradient">{language === 'ar' ? ' بأيدي محترفة' : ' with Expertise'}</span>
              </motion.h2>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-gray-600 mb-8 leading-relaxed"
            >
              {language === 'ar'
                ? 'شركة مكة البركة للمقاولات العامة تأسست في جمهورية مصر العربية وذلك لتلبية احتياج المنطقة العربية في النهوض بالبنية التحتية ومواكبة التطور العالمي وذلك بعد توجيه الدولة المصرية للنهوض بالبنية التحتية لتبدأ العمل بمشروع قناة السويس الجديدة واكتسبت الأفضلية والتميز بين الشركات ونزاهة التعامل مع الكل وقد تخصصت الشركة منذ انطلاقها في أعمال الطرق والمحاجر والأعمال الترابية وشاركت في العديد من المشاريع القومية العملاقة.'
                : 'Mecca El Baraka For Construction was established in the Arab Republic of Egypt to meet the needs of the Arab region in advancing infrastructure and keeping pace with global development, following the Egyptian state\'s direction to advance infrastructure to begin work on the New Suez Canal project. The company has gained preference and excellence among companies and integrity in dealing with everyone. Since its launch, the company has specialized in road works, quarries, and earthworks, and has participated in many giant national projects.'}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg text-gray-600 mb-8 leading-relaxed"
            >
              {language === 'ar'
                ? 'نقدم حلولاً متكاملة تشمل المقاولات العامة، استخراج وتجهيز المحاجر، البنية التحتية، والتصميم المعماري. نلتزم بأعلى معايير الجودة والسلامة ونستخدم أحدث التقنيات والمواد في جميع مشاريعنا.'
                : 'We offer integrated solutions including general contracting, quarry extraction and processing, infrastructure, and architectural design. We adhere to the highest standards of quality and safety, using the latest technologies and materials in all our projects.'}
            </motion.p>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="grid grid-cols-2 gap-4 mb-8"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                  className="flex items-start space-x-3 rtl:space-x-reverse"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 rtl:space-x-reverse text-primary-600 hover:text-primary-700 font-semibold transition-colors"
              >
                <span>{language === 'ar' ? 'اقرأ المزيد عنا' : 'Read More About Us'}</span>
                <FaArrowLeft className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt={language === 'ar' ? 'فريق العمل في شركة مكة البركة' : 'Makkah Al Baraka Team'}
                className="w-full h-96 md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              
              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">11+</div>
                  <div className="text-sm text-gray-600">{language === 'ar' ? 'سنة خبرة' : 'Years Experience'}</div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary-400 rounded-full opacity-20"></div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-primary-400 rounded-full opacity-20"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview; 